<template>
  <div class="main-div vld-parent">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div v-if="!isLoading" class="vasion-main-inner-div">
      <div id="header">
        <div class="vasion-page-title header-text">
          Templates
        </div>
        <VasionButton v-if="tableData && tableData.length > 0" classProp="primary" @vasionButtonClicked="newTemplate">
          New Template
        </VasionButton>
      </div>
      <div v-if="!tableData || tableData.length === 0" class="empty-templates">
        <VasionFileIconScalable id="file-icon" />
        <div class="text-style-1">
          You don't have any created templates.
        </div>
        <div class="vasion-page-subheader-light">
          Documents you save as templates will appear here.
        </div>
        <VasionButton
          id="create-new-template"
          classProp="primary"
          @vasionButtonClicked="newTemplate"
        >
          <span class="vasion-button-plus">+</span>Create New Template
        </VasionButton>
      </div>
      <div v-else class="inboxGridDiv topPadding">
        <div>
          <VasionTable
            :headerColumns="['_FormID_', '_VasionIcon_', 'Template Names', '_VasionEllipsisButton_']"
            :hideColumns="['_FormID_']"
            :tableRows="tableData"
            :ellipsisButtonConfig="ellipsisButtonConfig"
            :supportSorting="true"
            @ellipsis-button-clicked="ellipsisButtonClicked"
            @vasion-selection="editTemplate"
            @vasion-column-sort="sortColumns"
          />
        </div>
      </div>
    </div>

    <VasionSnackbar
      id="signature-template-snack"
      :showSnackbarBool="showSnackbarBool"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />

    <VasionGeneralModal
      id="vasion-delete-template-modal"
      :hideButtons="true"
      :modalType="'slot'"
      buttonGroup="single"
      :sync="showDeleteDialog"
      @noButtonClick="showDeleteDialog = false"
    >
      <VasionConfirmationDialog
        :message="deletePromptMessage"
        @noButtonClick="showDeleteDialog = false"
        @yesButtonClick="deleteTemplate()"
      />
    </VasionGeneralModal>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import 'vue-loading-overlay/dist/vue-loading.css';
import { tableSorterWithTransform } from '@/store/helperModules/common.module'

export default {
  name: 'TheSignatureTemplates',
  components: {
    Loading,
  },
  data: function () {
    return {
      activeNameToDelete: '',
      activeIdToDelete: 0,
      deletePromptMessage: '',
      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
      showDeleteDialog: false,
      showSnackbarBool: false,
      snackbarImage: false,
      snackbarSubTitle: '',
      snackbarTitle: '',
      tableData: [],
    }
  },
  computed: {
    ellipsisButtonConfig() { return this.$store.state.security.ellipsisButtonConfig },
  },
  async created() {
    this.isLoading = true

    await Promise.all([
      this.$store.dispatch('security/setEllipsisButtonConfig', 'signature-templates'),
      this.$store.dispatch('digitalSignature/getSignatureDocumentTemplates'),
    ])
    await this.refreshTable()

    this.isLoading = false
  },
  methods: {
    async deleteTemplate() {
      const response = await this.$store.dispatch('document/deleteSignatureDocumentTemplate', this.activeIdToDelete.toString())
      this.showDeleteDialog = false

      if (response.data.Value === 'True') {
        this.snackbarImage = true
        this.snackbarTitle = 'Delete Successful'
        this.snackbarSubTitle = `Successfully deleted "${this.activeNameToDelete}".`
      } else {
        this.snackbarImage = false
        this.snackbarTitle = 'Error Deleting'
        this.snackbarSubTitle = `There was an error deleting "${this.activeNameToDelete}".\nPlease contact your administrator if the problem persists.`
      }
      this.showSnackbarBool = true

      await this.$store.dispatch('digitalSignature/getSignatureDocumentTemplates')
      this.refreshTable()
    },
    async editTemplate(templateID, sendOnly = false) {
      // This is to update the ID if the row was clicked, because the row returns an object
      this.isLoading = true
      const correctID = typeof templateID === 'object' ? templateID.Values[0] : templateID

      const response = await this.$store.dispatch('digitalSignature/getSignatureDocumentTemplate', correctID)
      let data = {}
      data.annotationsObject = {}
      data.assignedUserSignatureList = []
      data.assignedZonesSignatureList = response.zones.map(zone => {
        return {
          objectId: zone.annotationID,
          pageNumber: zone.pageNumber,
          sigDocTemplateRoleName: zone.sigDocTemplateRoleName,
        }
      })
      data.editTemplateId = correctID
      data.isTemplate = true
      data.totalPages = 0
      for (const index in response) {
        data[index] = response[index]
      }
      await this.$store.dispatch('digitalSignature/setActiveDocument', data)

      if (sendOnly) {
        this.$router.push({ name: 'SignatureEditDocument', params: { documentId: correctID, pairedDownView: true } })
      } else {
        this.$router.push({ name: 'NewDocument' })
      }
      this.isLoading = false
    },
    ellipsisButtonClicked(payload) {
      if (payload?.item?.Values[0]) {
        const [id, , name] = payload.item.Values
        switch (payload.buttonId) {
          case 'use-signature-template-button':
            this.editTemplate(id, true)
            break
          case 'edit-signature-template-button':
            this.editTemplate(id)
            break;
          case 'delete-signature-template-button':
            this.deletePromptMessage = `Are you sure you want to delete "${name}"?`
            this.activeNameToDelete = name
            this.activeIdToDelete = id
            this.showDeleteDialog = true
            break;
          default:
            break;
        }
      }
    },
    newTemplate() {
      const data = {
        templateName: '',
        filePath: '',
        totalPages: 0,
        annotationsObject: null,
        assignedUserSignatureList: null,
        isTemplate: true,
        editTemplateId: 0,
      }
      this.$store.dispatch('digitalSignature/setActiveDocument', data)
      this.$router.push({ name: 'NewDocument' })
    },
    async refreshTable() {
      this.isLoading = true

      const { Values } = this.$store.state.digitalSignature.templateData
      if (Values === undefined) {
        this.tableData = []
      } else {
        this.tableData = Values.map(x => {
          return { ValueType: 'string', Values: [x.iID, 'VasionFileIcon', x.sName, 'VasionEllipsisIcon'] }
        })
      }

      this.isLoading = false
    },
    async sortColumns(options) {
      let columnLegend = {
        'Template Names': 2,
      }

      this.tableData.sort(tableSorterWithTransform(
        x => x.Values[columnLegend[options.columnName]],
        options.ascending
      ))
    }
  },
}
</script>

<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';
  #header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 5px 0px;
  }

  .inboxGridDiv {
    height: calc(100vh - 130px);
    overflow: auto;
  }

  .main-div {
    width: 100%;
    background-color: $white;
  }

  .vasion-ellipsis-menu {
    fill: $grey-500;
    margin-right: 50px !important;
  }

  .header-text {
    margin: 0px;
  }
  .empty-templates{
    margin-top: 100px;
    display: flex;
    align-items: center;
    flex-direction: column;
  }
  .singleWideColumn{
    min-width: 10px;
  }

  .text-style-1{
    margin: 10px;
  }

  #file-icon{
    fill: #d6d8da;
    height: 124px;
    width: 124px;
    margin-bottom: 56px;
  }

  #create-new-template{
    margin-top: 30px;
  }

  @media(max-width: $phone){
    #file-icon{
      margin-top: 40px;
    }
    .text-style-1{
      text-align: center;
      font-size: 18px;
    }
    .vasion-page-subheader-light{
      font-size: 14px;
      text-align: center;
    }
  }
</style>
